import * as _ from 'lodash';
import qs from 'qs';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { FunctionComponent, ReactNode, RefObject } from 'react';
import type { InjectedRouter } from 'react-router';

import { window } from '../../../globals';
import { testPasswordStrength } from '../../../helpers/passwordStrength';
import { useFetchPendingSocialAuth } from '../../../hooks/api/auth/social/useFetchPendingSocialAuth';
import { useSelectSocialOrg } from '../../../hooks/api/auth/social/useSelectSocialOrg';
import useAsyncStatus from '../../../hooks/useAsyncStatus';
import useInputValue from '../../../hooks/useInputValue';
import { hardRedirectTo } from '../../../utils';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { LoginOrgCandidate } from '../../../../shared/organizationV2';
import type { LoginNextResponse, LoginResponse } from '../../../../typings/api';
import type { FailedFetchResponse } from '../../../helpers/errors';
import type { PasswordStrength } from '../../../helpers/passwordStrength';
import type { AsyncStatus, RouterProps } from '../../../types';
import type { TextInputOnChange } from '../../generic/Forms/InputBase';
import type { OrgPickerOption } from '../form/OrgPicker';
import type { AuthPage } from '../index';

import {
  createOrganization,
  getPageFromPathname,
  getSSOAlertText,
  isSubmitBtnDisabled,
  login,
  loginNext,
  register,
} from './helpers';
import useFocusInputs from './useFocusInputs';
import useGetLoginMethods from './useGetLoginMethods';
import useInviteToken from './useInviteToken';
import useLdapAuthRequired from './useLdapAuthRequired';
import useLoginError from './useLoginError';
import useMfaFlow from './useMfaFlow';

export type AuthInput = {
  ref: RefObject<HTMLInputElement> | null;
  value: string;
  onChange: TextInputOnChange;
};

export type LoginStep = 'enterEmail' | 'selectOrg' | 'authenticate' | 'verifyMfa';

export type AuthContextState = {
  //  Which "page" we are on, which determines the UI to display
  page: AuthPage;
  //  'enterEmail' is where users enter their email, 'authenticate' is what happens next, 'verifyMfa' is for MFA verification.
  step: LoginStep;
  email: AuthInput;
  password: AuthInput;
  hidePasswordInput: boolean;
  isLdapAuthRequired: boolean;
  status: AsyncStatus;
  //  Displays the UI alerts
  alert: string | null;
  submitButton: {
    isDisabled: boolean;
    onSubmit: (code?: string) => void;
  };
  loginMethods: {
    bitbucket?: boolean;
    github?: boolean;
    gitlab?: boolean;
    google?: boolean;
  };
  //  REGISTER-SPECIFIC
  hasInvite: boolean;
  orgTitle: string | null;
  inviteToken?: string;
  // URL to redirect to after authentication (from query params, e.g. OIDC interaction endpoint)
  next?: string;
  passwordStrength?: PasswordStrength;
  samlURL: string | null;
  // MULTI-ORG SPECIFIC: (user, org) candidates returned by the backend
  pickerCandidates: LoginOrgCandidate[];
  selectedUserId: number | null;
  orgPickerOptions: OrgPickerOption[];
  onSelectUserId: (userId: number) => void;
  onResetOrgSelection: () => void;
  onReturnToOrgPicker: () => void;
  // MFA-SPECIFIC
  mfaCode: AuthInput;
  mfaMethod: MfaMethodType | null;
  useBackupCode: boolean;
  toggleBackupCode: () => void;
  statusMethods: {
    onPending: () => void;
    onSuccess: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type from useAsyncStatus
    onError: (failedResponse: any) => string;
    clearError: () => void;
  };
};

/**
 * Context allows us to store values (ie: state) and share it across components.
 * By creating this top-level state container, we can then either wrap children
 * in `<AuthContext.Consumer>`, or children can use the `useContext` hook,
 * which gives those components access to this state without needing to explicitly pass them as props.
 * Read more about React Context API here: https://reactjs.org/docs/context.html
 */
export const AuthContext = createContext<AuthContextState>({} as AuthContextState);

//  This is a hook which allows us to use this context in any component.
//  If the `value` in <AuthContextProvider /> ever changes, components
//  using this hook will be re-rendered with the updated state.
export const useAuthContext = () => useContext<AuthContextState>(AuthContext);

type AuthContextProviderProps = {
  children: ReactNode;
  location: RouterProps['location'];
  inviteToken?: string;
  router?: InjectedRouter; // Optional, since it only exists on the login page.
};

/**
 * Wrapper for `<AuthContext.Provider>`, which hits `/account/login_state` on mount,
 * then stores the results in useState which is passed into AuthContext.
 *
 * @param {ReactNode} children - Child component tree root which will be provided this context.
 * @param {RouterProps['location']} location - The current location object.
 * @param {string} [inviteToken] - The invite token that was given to the user to register.
 * @param {InjectedRouter} router - The React app router.
 */
const AuthContextProvider: FunctionComponent<AuthContextProviderProps> = ({
  children,
  location,
  inviteToken,
  router,
}) => {
  //  Note that since pathname won't change (without re-rendering the parent & re-running this hook), we don't need to store it with useState.
  const page = getPageFromPathname(location.pathname);
  const next = location.query?.next;
  //  States
  const [step, setStep] = useState<LoginStep>('enterEmail');
  const [alert, setAlert] = useState<string | null>(null);
  const [hidePasswordInput, setHidePasswordInput] = useState(false);
  const [email, onEmailChange] = useInputValue(location.query?.email || ''); // If there is an email in the query param, we can set it as the initial value.
  const [password, onPasswordChange] = useInputValue('');
  const { emailInputRef, passwordInputRef } = useFocusInputs();
  const { status, ...statusMethods } = useAsyncStatus();
  const { clearError } = statusMethods;
  const [samlURL, setSamlURL] = useState<string | null>(null);
  const [pickerCandidates, setPickerCandidates] = useState<LoginOrgCandidate[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  // Multi-org social login: when the OAuth callback hands us off with ?pendingSocial=1, the picker
  // submit goes to /account/social/select-org instead of /login/next.
  const [isPendingSocial, setIsPendingSocial] = useState(false);

  // Tear down picker state and return to the email step
  const resetToLoginEmailStep = useCallback(() => {
    setPickerCandidates([]);
    setSelectedUserId(null);
    onEmailChange('');
    setStep('enterEmail');
    if (isPendingSocial) {
      setIsPendingSocial(false);
      router?.push('/auth/login');
    }
  }, [onEmailChange, isPendingSocial, router]);

  const onResetOrgSelection = useCallback(() => {
    resetToLoginEmailStep();
    setAlert(null);
    clearError();
  }, [resetToLoginEmailStep, clearError]);

  // Back-navigation from the authenticate step to the picker. Clears state the previous
  // /login/next set password input visibility, SAML URL, SSO alert
  const onReturnToOrgPicker = useCallback(() => {
    setSelectedUserId(null);
    onPasswordChange('');
    setHidePasswordInput(false);
    setSamlURL(null);
    setAlert(null);
    clearError();
    setStep('selectOrg');
  }, [onPasswordChange, clearError]);

  // MFA
  const {
    mfaCode,
    mfaMethod,
    useBackupCode,
    toggleBackupCode,
    onVerifyMfa,
    setMfaMethod,
  } = useMfaFlow({
    statusMethods,
    onPasswordChange,
    setStep,
    setAlert,
  });

  //  LOGIN ONLY
  useLoginError(statusMethods.onError, location.query);
  const isLdapAuthRequired = useLdapAuthRequired(setAlert, location.query);
  const [loginMethods, onUpdateLoginMethods] = useGetLoginMethods(isLdapAuthRequired);

  //  REGISTER ONLY
  const { orgTitle, invitedEmail, postSignupRedirectUri } = useInviteToken(setAlert, inviteToken);

  // Consider the user to have an invite only if we've fetched the invited email (which means the token is valid).
  const hasInvite = !!invitedEmail;

  useEffect(() => {
    if (invitedEmail) {
      onEmailChange(invitedEmail);
    }
  }, [invitedEmail, onEmailChange]);

  // Multi-org social login: the OAuth callback redirects here with ?pendingSocial=1 when the email
  // matches users in more than one org. We pull the candidate orgs + email from the server-side
  // pending-auth record and render the org picker.
  const pendingSocialQuery = useFetchPendingSocialAuth({
    enabled: location.query?.pendingSocial === '1',
  });
  useEffect(() => {
    if (pendingSocialQuery.error) {
      statusMethods.onError(pendingSocialQuery.error);
      router?.push('/auth/login');
      return;
    }
    if (!pendingSocialQuery.data) {
      return;
    }
    onEmailChange(pendingSocialQuery.data.email);
    setPickerCandidates(pendingSocialQuery.data.candidates);
    setSelectedUserId(null);
    setIsPendingSocial(true);
    setStep('selectOrg');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSocialQuery.data, pendingSocialQuery.error]);

  const selectSocialOrgMutation = useSelectSocialOrg();

  //  Handles what happens when a user is trying to log into an SSO-only org.
  const handleRequireSSO = (response: LoginNextResponse | LoginResponse) => {
    //  This won't ever happen, it's just to please TS
    if (response.action !== 'require_sso') {
      return;
    }
    const isGoogleSSO = response.method === 'google';
    const samlRedirectURL: string | null = response.method === 'saml'
      ? response.samlURL
      : null;

    if (response.method !== 'ldap') {
      setHidePasswordInput(true);
    }

    if (response.method) {
      setAlert(
        getSSOAlertText(isGoogleSSO),
      );
    }

    //  Handle SAML redirect if this is a loginNext request.
    if (samlRedirectURL) {
      return hardRedirectTo(samlRedirectURL);
    }

    statusMethods.onSuccess();
    //  Update the login methods
    onUpdateLoginMethods({
      github: false,
      gitlab: false,
      bitbucket: false,
      google: isGoogleSSO,
    });
  };

  const onLogin = async (loc: RouterProps['location']) => {
    statusMethods.onPending();
    try {
      const response: LoginResponse = await login({
        username: email,
        password,
        next: loc.query.next,
        subdomain: loc.query.subdomain,
      });
      switch (response.action) {
        case 'redirect':
        case 'logged_in':
          hardRedirectTo(response?.to ?? '');
          return;
        case 'require_sso':
          handleRequireSSO(response);
          break;
        case 'mfa_required':
          // Transition to MFA verification step
          // Note: userId is stored in session by the backend
          setStep('verifyMfa');
          setMfaMethod(response?.mfaMethod || null);
          setAlert(null);
          break;
        default:
          break;
      }
      statusMethods.onSuccess();
    } catch (error) {
      statusMethods.onError(error);
    }
  };

  // Makes a request to GET /account/login/next and handles the response, used for both signing in and registering.
  const onLoginNextStep = async (loc: RouterProps['location']) => {
    //  Inform the UI that we are loading, remove any stale Alerts
    statusMethods.onPending();
    setAlert(null);
    try {
      // After the picker, derive orgId from the picked user/org candidate; the /login/next
      // contract still takes selectedOrgId.
      const selectedOrgId = pickerCandidates.find(c => c.userId === selectedUserId)?.id;
      const response: LoginNextResponse = await loginNext(
        email,
        loc.query.subdomain,
        loc.query.inviteToken,
        selectedOrgId,
      );
      switch (response.action) {
        case 'redirect':
          hardRedirectTo(response?.to ?? '/auth/register');
          return;
        case 'clear_subdomain':
        // Do not carry on to the next step.
          setAlert(response.message);
          statusMethods.onSuccess();
          // Clear out the subdomain query parameter.
          router.push(loc.pathname);
          return;
        case 'select_org':
          //  Backend found more than one org matching this email/username. Show the picker;
          //  the user's choice will be re-submitted to /login/next via selectedOrgId.
          setPickerCandidates(response.candidates);
          setSelectedUserId(null);
          setStep('selectOrg');
          statusMethods.onSuccess();
          return;
        case 'signup':
        // If we're signing in and the response is to sign up, redirect to the registration page. Otherwise reveal the
        // password field for the user to choose a password.
          if (page !== 'register') {
            const queryParams = qs.stringify({ email: email || undefined, next: loc.query.next }, { skipNulls: true });
            hardRedirectTo(`/auth/register${queryParams ? `?${queryParams}` : ''}`);
            return;
          }
          break;
        case 'require_sso':
          handleRequireSSO(response);
          break;
          //  For password/non-fancy login users, we just show the next step where they input their password.
        case 'password':
          if (response.samlURL) {
          //  Display a "Login with SAML button" if there is a samlURL.
            setSamlURL(response.samlURL);
          }
          break;
        default:
          break;
      }
      setStep('authenticate');
      statusMethods.onSuccess();
    } catch (error) {
      statusMethods.onError(error);
    }
  };

  const onRegister = async () => {
    statusMethods.onPending();
    try {
      const response = await register({
        email,
        password,
        inviteToken,
        next,
      });
      let redirectTo = '/';
      if (!window.on_prem && !response.data?.organizationId) {
        // If not on prem and user is not already part of an organization, then create a new organization with the email as name
        await createOrganization(email, email);
        redirectTo = '/projects/import';
      }
      if (postSignupRedirectUri) {
        // Invited from an OIDC client app, redirect there so it can initiate its own OIDC auth flow with the IDP session we just created
        redirectTo = postSignupRedirectUri;
      }

      if (response.data?.next) {
        // Server-validated redirect URL (e.g. OIDC interaction endpoint)
        redirectTo = response.data.next;
      }

      statusMethods.onSuccess();
      //  Server will log the user in, we just redirect them to the next page.
      hardRedirectTo(redirectTo);
    } catch (error) {
      statusMethods.onError(error);
    }
  };

  // Multi-org social login: finalize the deferred login by posting the chosen user/org pair.
  const onSelectSocialOrg = async (loc: RouterProps['location']) => {
    if (selectedUserId == null) {
      return;
    }
    statusMethods.onPending();
    try {
      const nextQuery = loc.query?.next;
      const response = await selectSocialOrgMutation.mutateAsync({
        selectedUserId,
        next: _.isString(nextQuery) ? nextQuery : undefined,
      });
      if (response.data) {
        hardRedirectTo(response.data.to);
      }
    } catch (error) {
      // Drop the user back on the email step so the next attempt restarts the OAuth handshake instead of retrying against a dead handle.
      resetToLoginEmailStep();
      statusMethods.onError(error as FailedFetchResponse);
    }
  };

  //  Handle submit button click
  const onSubmitButtonClick = useCallback((code?: string) => {
    if (page === 'login') {
      if (step === 'enterEmail') {
        return onLoginNextStep(location);
      }
      if (step === 'selectOrg') {
        if (isPendingSocial) {
          return onSelectSocialOrg(location);
        }
        // Re-run /login/next with the chosen org; backend will respond with the next normal action.
        return onLoginNextStep(location);
      }
      if (step === 'verifyMfa') {
        // Pass the code from parameter (auto-submit) or current state
        return onVerifyMfa(location, code || mfaCode.value, useBackupCode);
      }
      return onLogin(location);
    }
    //  Register handle next button click
    if (step === 'enterEmail') {
      return onLoginNextStep(location);
    }
    if (step === 'selectOrg') {
      return onLoginNextStep(location);
    }
    return onRegister();
  }, [
    //  Note that we need this callback to be refreshed if email/password are used, so that the login helpers get the updated values.
    page,
    step,
    email,
    password,
    location,
    mfaCode.value,
    useBackupCode,
    onVerifyMfa,
    postSignupRedirectUri,
    next,
    pickerCandidates,
    selectedUserId,
    isPendingSocial,
  ]);

  // When the same orgId appears more than once (only happens in the social flow same OAuth identity linked to two FOSSA users in the same org )
  // display the user's email under those entries so the picker can be disambiguated.
  const orgPickerOptions = useMemo<OrgPickerOption[]>(() => {
    const orgIdCounts = new Map<number, number>();
    pickerCandidates?.forEach(candidate => {
      orgIdCounts.set(candidate.id, (orgIdCounts.get(candidate.id) ?? 0) + 1);
    });
    return pickerCandidates?.map(candidate => ({
      value: candidate.userId,
      label: candidate.title,
      secondaryLabel: (orgIdCounts.get(candidate.id) ?? 0) > 1 ? candidate.email : undefined,
    }));
  }, [pickerCandidates]);

  //  useMemo can help us avoid needless re-renders. This says "only update if one of these dependencies changes".
  const value: AuthContextState = useMemo(() => ({
    page,
    step,
    alert,
    email: {
      ref: emailInputRef,
      value: email,
      onChange: onEmailChange,
    },
    password: {
      ref: passwordInputRef,
      value: password,
      onChange: onPasswordChange,
    },
    hidePasswordInput,
    isLdapAuthRequired,
    submitButton: {
      isDisabled: isSubmitBtnDisabled({
        step,
        email,
        password,
        hidePasswordInput,
        mfaCode: mfaCode.value,
        useBackupCode,
        selectedUserId,
      }),
      onSubmit: onSubmitButtonClick,
    },
    status,
    loginMethods,
    hasInvite,
    orgTitle,
    inviteToken,
    next,
    passwordStrength: page === 'register'
      ? testPasswordStrength(password)
      : undefined, // No need to run password through the strength meter on the login page.
    samlURL,
    pickerCandidates,
    selectedUserId,
    orgPickerOptions,
    onSelectUserId: setSelectedUserId,
    onResetOrgSelection,
    onReturnToOrgPicker,
    mfaCode,
    mfaMethod,
    useBackupCode,
    toggleBackupCode,
    statusMethods,
  }), [
    page,
    step,
    alert,
    email,
    password,
    hidePasswordInput,
    isLdapAuthRequired,
    status,
    loginMethods,
    onSubmitButtonClick,
    onEmailChange,
    onPasswordChange,
    hasInvite,
    orgTitle,
    inviteToken,
    next,
    emailInputRef,
    passwordInputRef,
    samlURL,
    pickerCandidates,
    selectedUserId,
    orgPickerOptions,
    onResetOrgSelection,
    onReturnToOrgPicker,
    mfaCode,
    mfaMethod,
    useBackupCode,
    toggleBackupCode,
    statusMethods,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
